/**
 * PatientIDCaptureStep - Orchestrates photo ID and insurance card capture
 *
 * This is a check-in flow step that appears before the form.
 * It walks the patient through capturing their photo ID and/or insurance card,
 * uploads images to Firebase Storage, and passes extracted data + image URLs
 * to the parent component for downstream use.
 *
 * Sub-stages: 'photo-id' → 'insurance-card' → done
 * Each sub-stage is skipped if the corresponding PET flag is false.
 */

import React, { useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Alert,
  CircularProgress,
} from '@mui/material';
import { contentCardSx } from '../../styles/standaloneLayoutTokens';
import {
  Badge,
  CreditCard,
  CheckCircle,
  ArrowForward,
} from '@mui/icons-material';
import { storage, uploadImageToStorage as uploadImageToStorageShared } from '@healthdesk/shared-services';
import IDScanner from '../../features/chartmind/components/chartmind/IDScanner';

const uploadImageToStorage = (base64Image, storagePath) =>
  uploadImageToStorageShared(storage, base64Image, storagePath);

/**
 * @param {Object} props
 * @param {boolean} props.requirePhotoID - Whether photo ID capture is required
 * @param {boolean} props.requireInsuranceCard - Whether insurance card capture is required
 * @param {string} props.clinicId - Clinic ID for storage path
 * @param {string} props.templateId - PET template ID for storage path
 * @param {function} props.onComplete - Called with { idScan, insuranceScan } when all captures are done
 */
const PatientIDCaptureStep = ({
  requirePhotoID = false,
  requireInsuranceCard = false,
  clinicId,
  templateId,
  onComplete,
}) => {
  // Build the list of sub-stages based on PET flags
  const stages = [];
  if (requirePhotoID) stages.push('photo-id');
  if (requireInsuranceCard) stages.push('insurance-card');

  const [currentStageIndex, setCurrentStageIndex] = useState(0);
  const [scannerOpen, setScannerOpen] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);

  // Accumulated results
  const [idScanResult, setIdScanResult] = useState(null);
  const [insuranceScanResult, setInsuranceScanResult] = useState(null);

  const currentStage = stages[currentStageIndex] || null;
  const sessionId = `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

  const advanceOrFinish = useCallback((idData, insData) => {
    const nextIndex = currentStageIndex + 1;
    if (nextIndex < stages.length) {
      setCurrentStageIndex(nextIndex);
      setScannerOpen(true);
    } else {
      onComplete?.({
        idScan: idData || idScanResult,
        insuranceScan: insData || insuranceScanResult,
      });
    }
  }, [currentStageIndex, stages.length, onComplete, idScanResult, insuranceScanResult]);

  const handleExtracted = useCallback(async (data) => {
    setScannerOpen(false);
    setUploading(true);
    setUploadError(null);

    try {
      const isInsurance = currentStage === 'insurance-card';
      const fileName = isInsurance ? 'insurance-card.jpg' : 'photo-id.jpg';
      const storagePath = `clinics/${clinicId}/checkin-captures/${sessionId}/${fileName}`;

      let imageUrl = null;
      if (data.imageBase64) {
        imageUrl = await uploadImageToStorage(data.imageBase64, storagePath);
      }

      const { imageBase64, ...extractedFields } = data;
      const result = {
        ...extractedFields,
        imageUrl,
        capturedAt: new Date().toISOString(),
      };

      if (isInsurance) {
        setInsuranceScanResult(result);
        advanceOrFinish(idScanResult, result);
      } else {
        setIdScanResult(result);
        advanceOrFinish(result, insuranceScanResult);
      }
    } catch (err) {
      console.error('[PatientIDCaptureStep] Error processing capture:', err);
      setUploadError('Failed to save the image. Please try again.');
      setScannerOpen(true);
    } finally {
      setUploading(false);
    }
  }, [currentStage, clinicId, sessionId, advanceOrFinish, idScanResult, insuranceScanResult]);

  const handleScannerClose = useCallback(() => {
    setScannerOpen(false);
  }, []);

  const handleReopen = useCallback(() => {
    setUploadError(null);
    setScannerOpen(true);
  }, []);

  if (!currentStage) {
    // No stages — should not happen, but handle gracefully
    onComplete?.({ idScan: null, insuranceScan: null });
    return null;
  }

  const isInsuranceStage = currentStage === 'insurance-card';

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto', py: 3, px: 2 }}>
      {/* Step indicator when both stages are needed */}
      {stages.length > 1 && (
        <Stepper activeStep={currentStageIndex} sx={{ mb: 3 }}>
          {stages.map((stage) => (
            <Step key={stage}>
              <StepLabel>
                {stage === 'photo-id' ? 'Photo ID' : 'Insurance Card'}
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      )}

      {/* Header */}
      <Paper elevation={0} sx={{ ...contentCardSx, p: 3, textAlign: 'center', mb: 2 }}>
        {isInsuranceStage ? (
          <>
            <CreditCard sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
            <Typography variant="h5" gutterBottom>
              Scan Your Insurance Card
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Please take a clear photo of the front of your insurance card.
              We'll extract your plan and member information automatically.
            </Typography>
          </>
        ) : (
          <>
            <Badge sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
            <Typography variant="h5" gutterBottom>
              Scan Your Photo ID
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Please take a clear photo of your government-issued ID
              (driver's license, passport, etc.).
              Your name and date of birth will be filled in automatically.
            </Typography>
          </>
        )}
      </Paper>

      {/* Uploading indicator */}
      {uploading && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <CircularProgress size={48} sx={{ mb: 2 }} />
          <Typography variant="body1">Saving image...</Typography>
        </Box>
      )}

      {/* Upload error */}
      {uploadError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {uploadError}
          <Button size="small" onClick={handleReopen} sx={{ ml: 1 }}>
            Try Again
          </Button>
        </Alert>
      )}

      {/* Completed sub-stage summary */}
      {idScanResult && currentStage === 'insurance-card' && (
        <Alert severity="success" icon={<CheckCircle />} sx={{ mb: 2 }}>
          Photo ID captured successfully — {idScanResult.name}
          {idScanResult.dob && ` (DOB: ${idScanResult.dob})`}
        </Alert>
      )}

      {/* Show "Open Scanner" button if scanner was closed without completing */}
      {!scannerOpen && !uploading && (
        <Box sx={{ textAlign: 'center', mt: 3 }}>
          <Button
            variant="contained"
            size="large"
            startIcon={isInsuranceStage ? <CreditCard /> : <Badge />}
            onClick={handleReopen}
          >
            {isInsuranceStage ? 'Open Insurance Scanner' : 'Open ID Scanner'}
          </Button>
        </Box>
      )}

      {/* IDScanner dialog */}
      <IDScanner
        open={scannerOpen}
        mode={isInsuranceStage ? 'insurance' : 'id'}
        onPatientExtracted={handleExtracted}
        onClose={handleScannerClose}
      />
    </Box>
  );
};

export default PatientIDCaptureStep;
